/**
 * Shared layout for /terms, /privacy, /refund-policy.
 *
 * - Sticky-readable max-width, white background, premium typography
 * - Section TOC on desktop
 * - "Last updated" badge
 * - Back-to-home + footer
 * - SEO meta tags via react-helmet-async (already installed)
 */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { SiteFooter } from "../../components/marketing/SiteFooter";

export function LegalPageLayout({ title, subtitle, lastUpdated, sections = [], children }) {
  useEffect(() => {
    // Lightweight SEO — set document title + meta description per page mount.
    document.title = `${title} · Ashra AI`;
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    if (subtitle) setMeta("description", subtitle);
    setMeta("robots", "index,follow");
  }, [title, subtitle]);

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid={`legal-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      {/* Top bar */}
      <header className="border-b border-zinc-200 sticky top-0 z-10 bg-white/90 backdrop-blur">
        <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-900 hover:opacity-80" data-testid="legal-home-link">
            <ArrowLeft className="h-4 w-4" /> Back to Ashra AI
          </Link>
          <div className="flex items-center gap-4 text-xs text-zinc-500">
            <Link to="/terms" className="hover:text-zinc-900">Terms</Link>
            <Link to="/privacy" className="hover:text-zinc-900">Privacy</Link>
            <Link to="/refund-policy" className="hover:text-zinc-900">Refund</Link>
          </div>
        </div>
      </header>

      {/* Page hero */}
      <section className="mx-auto max-w-4xl px-6 pt-16 pb-10">
        <div className="inline-flex items-center gap-2 rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700 mb-5">
          <FileText className="h-3.5 w-3.5" /> Legal
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900 mb-3">{title}</h1>
        {subtitle && <p className="text-base text-zinc-600 max-w-2xl">{subtitle}</p>}
        {lastUpdated && (
          <p className="mt-4 text-xs text-zinc-500">Last updated: <span className="font-semibold text-zinc-700">{lastUpdated}</span></p>
        )}
      </section>

      {/* Body — TOC + content */}
      <section className="mx-auto max-w-4xl px-6 pb-24">
        <div className="grid lg:grid-cols-[200px_1fr] gap-10">
          {sections.length > 0 && (
            <aside className="hidden lg:block">
              <div className="sticky top-24">
                <p className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold mb-3">On this page</p>
                <nav className="space-y-1.5">
                  {sections.map((s) => (
                    <a key={s.id} href={`#${s.id}`} className="block text-sm text-zinc-600 hover:text-violet-700 transition leading-snug">
                      {s.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>
          )}
          <article className="prose-ashra">
            {children}
          </article>
        </div>
      </section>

      <SiteFooter />

      <style>{`
        .prose-ashra { color:#27272a; font-size:15px; line-height:1.75; }
        .prose-ashra h2 { font-size:24px; font-weight:700; color:#18181b; margin:48px 0 16px; letter-spacing:-0.01em; scroll-margin-top:96px; }
        .prose-ashra h3 { font-size:17px; font-weight:600; color:#27272a; margin:32px 0 10px; }
        .prose-ashra p { margin:0 0 16px; }
        .prose-ashra ul { margin:0 0 16px 0; padding-left:22px; list-style:disc; }
        .prose-ashra li { margin:6px 0; }
        .prose-ashra a { color:#7c3aed; text-decoration:underline; text-underline-offset:3px; }
        .prose-ashra strong { color:#18181b; font-weight:600; }
        .prose-ashra code { background:#F4F4F5; padding:2px 6px; border-radius:4px; font-size:13px; }
      `}</style>
    </div>
  );
}

/** Convenience component for legal page sections that auto-register in the TOC. */
export function LegalSection({ id, title, children }) {
  return (
    <section id={id}>
      <h2>{title}</h2>
      {children}
    </section>
  );
}
